import type { EmailPulseColumnMetadata } from './email-pulse-column-metadata.types';
import { EMAIL_PULSE_DEFINITIONS } from '../email-pulse-definitions/email-pulse-definitions';

/**
 * Centralized column metadata registry for Email Pulse data grids.
 * This is the single source of truth for all common columns shared across
 * Messages, Offers, Orders, Gift Cards, and Security tabs.
 *
 * Each entry defines:
 * - field: The data field name
 * - definition: Reference to the metric definition (contains label and description)
 * - minWidth: Minimum column width
 * - flex: Flex value for column sizing
 * - hideable: Whether the column can be hidden
 * - sortable: Whether the column can be sorted
 * - defaultVisible: Whether the column is visible by default
 */
export const EMAIL_PULSE_COLUMN_METADATA: Record<
  string,
  EmailPulseColumnMetadata
> = {
  // Rate columns first
  deliveryRate: {
    field: 'deliveryRate',
    definition: EMAIL_PULSE_DEFINITIONS.deliveryRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  openRate: {
    field: 'openRate',
    definition: EMAIL_PULSE_DEFINITIONS.openRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  clickRate: {
    field: 'clickRate',
    definition: EMAIL_PULSE_DEFINITIONS.clickRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  clickThroughRate: {
    field: 'clickThroughRate',
    definition: EMAIL_PULSE_DEFINITIONS.clickThroughRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  bounceRate: {
    field: 'bounceRate',
    definition: EMAIL_PULSE_DEFINITIONS.bounceRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  unsubscribeRate: {
    field: 'unsubscribeRate',
    definition: EMAIL_PULSE_DEFINITIONS.unsubscribeRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  spamReportRate: {
    field: 'spamReportRate',
    definition: EMAIL_PULSE_DEFINITIONS.spamReportRate,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  // Absolute count columns
  sent: {
    field: 'sent',
    definition: EMAIL_PULSE_DEFINITIONS.emailsSent,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  delivered: {
    field: 'delivered',
    definition: EMAIL_PULSE_DEFINITIONS.emailsDelivered,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: true,
  },
  emailsDropped: {
    field: 'emailsDropped',
    definition: EMAIL_PULSE_DEFINITIONS.emailsDropped,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  emailsOpened: {
    field: 'emailsOpened',
    definition: EMAIL_PULSE_DEFINITIONS.emailsOpened,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  uniqueOpens: {
    field: 'uniqueOpens',
    definition: EMAIL_PULSE_DEFINITIONS.uniqueOpens,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  clicks: {
    field: 'clicks',
    definition: EMAIL_PULSE_DEFINITIONS.clicks,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  uniqueClicks: {
    field: 'uniqueClicks',
    definition: EMAIL_PULSE_DEFINITIONS.uniqueClicks,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  emailsBounced: {
    field: 'emailsBounced',
    definition: EMAIL_PULSE_DEFINITIONS.emailsBounced,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  unsubscribes: {
    field: 'unsubscribes',
    definition: EMAIL_PULSE_DEFINITIONS.unsubscribes,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  spamReports: {
    field: 'spamReports',
    definition: EMAIL_PULSE_DEFINITIONS.spamReports,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  emailsDeferred: {
    field: 'emailsDeferred',
    definition: EMAIL_PULSE_DEFINITIONS.emailsDeferred,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
  uniqueEmailsDeferred: {
    field: 'uniqueEmailsDeferred',
    definition: EMAIL_PULSE_DEFINITIONS.uniqueEmailsDeferred,
    minWidth: 150,
    flex: 1,
    hideable: true,
    sortable: true,
    defaultVisible: false,
  },
};
